import { useNavigate } from 'react-router-dom';
import { FolderOpenIcon } from '@heroicons/react/24/outline';
import { ThreeDotMenu } from '../Packet/Components/ThreeDotMenu';
import { Mixpanel } from '../../services/Mixpanel';

/**
 * Shared sidebar row for a folder — used for both owned folders and folders shared with you
 * ("Shared with me") so they share one look and feel. Clicking the row opens the folder page
 * (/c/:id); per-row actions live in the ⋯ menu.
 *
 * - ownerName: shown as a byline (folders shared with you); omit for the user's own.
 * - menuButtons: array of <DropdownButton> for the ⋯ menu (Share/Rename/Delete, or Leave folder).
 */
export const CollectionSidebarRow = ({ collection, ownerName, menuButtons, location }) => {
  const navigate = useNavigate();
  const openCollection = () => {
    Mixpanel.track('Collection Opened From Sidebar', {
      location,
      collection_id: collection.id,
      collection_title: collection.title,
    });
    navigate(`/c/${collection.id}`);
  };
  return (
    <div
      role='button'
      tabIndex={0}
      aria-label={`Folder: ${collection.title}`}
      onClick={(e) => {
        // Don't navigate when interacting with the ⋯ menu (button or its items).
        if (e.target.closest('[data-menu-button]')) return;
        openCollection();
      }}
      onKeyDown={(e) => {
        // Row is keyboard-focusable (tabIndex=0); the ⋯ menu is separately focusable, so a
        // key event reaching here means the row itself is focused.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openCollection();
        }
      }}
      className='group flex justify-between items-center gap-x-2 text-gray-600 rounded-md p-2 hover:bg-gray-100 cursor-pointer'>
      <div className='flex items-center gap-x-2 min-w-0'>
        <FolderOpenIcon className='w-4 h-4 text-gray-500 flex-shrink-0' />
        <div className='flex flex-col min-w-0'>
          <span className='truncate'>{collection.title}</span>
          {ownerName && <span className='text-xs text-gray-400 truncate'>by {ownerName}</span>}
        </div>
      </div>
      <div className='flex items-center gap-x-1 flex-shrink-0'>
        {menuButtons?.length > 0 && (
          <ThreeDotMenu
            location={location}
            buttonClassName='hover:bg-gray-200 rounded-md p-1 group-hover:opacity-100 focus-visible:opacity-100 opacity-0'
            buttons={menuButtons}
          />
        )}
      </div>
    </div>
  );
};
